import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";

import { ipdElevatedInputSx } from "../../ipd/utils";
import { searchMedicines } from "../api/emergencyApi";

const DEBOUNCE_MS = 300;

/**
 * Type-to-search medicine picker for the structured Rx line (ticket 04). Backed
 * by the hospital's formulary search (`search_medicines/`). Free-solo on purpose:
 * a medicine not in the formulary can still be ordered — the ED never blocks an
 * order on a reference list. `value`/`onChange` are the medicine's name string;
 * the parent's line is the single source of truth.
 */
export default function EmergencyMedicineAutocomplete({ value, onChange, disabled }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const reqRef = useRef(0);

  useEffect(() => {
    const term = (value || "").trim();
    if (term.length < 2) {
      setOptions([]);
      return undefined;
    }
    const reqId = ++reqRef.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const rows = await searchMedicines(term);
        if (reqRef.current === reqId) setOptions(rows);
      } catch {
        if (reqRef.current === reqId) setOptions([]);
      } finally {
        if (reqRef.current === reqId) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <Autocomplete
      freeSolo
      size="small"
      disabled={disabled}
      options={options}
      loading={loading}
      inputValue={value || ""}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.term || "")}
      isOptionEqualToValue={(a, b) => (a?.id ?? a) === (b?.id ?? b)}
      filterOptions={(x) => x}
      onInputChange={(_, v, reason) => {
        if (reason !== "reset") onChange(v);
      }}
      onChange={(_, v) => onChange(typeof v === "string" ? v : v?.term || "")}
      noOptionsText={loading ? "Searching…" : "Type to search medicines"}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search medicine…"
          sx={ipdElevatedInputSx}
          inputProps={{ ...params.inputProps, "aria-label": "Medicine" }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={15} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
